import { Component, OnInit, OnDestroy, Optional, ChangeDetectionStrategy } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { AiConfigService } from '../../services/ai-config.service';
import { SettingsService } from '../../services/settings.service';

interface OptionConfigItem {
  name: string;
  display: string;
  description: string;
  value: boolean | string;
  textField?: boolean;
  hideCheckbox?: boolean;
  textFieldPlaceholder?: string;
}

@Component({
  selector: 'app-form-modal',
  templateUrl: './form-modal.component.html',
  styleUrls: ['./form-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormModalComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;

  // Edited as plain strings (starter options one per line) and written back through
  // AiConfigService.setAiOptions() on leave, so the greeting/handoff objects are replaced rather
  // than mutated and a blank field clears the feature instead of saving an empty greeting or chip.
  aiQuickReplies = false;
  aiGreetingText = '';
  aiGreetingOptions = '';
  aiHandoffLabel = '';

  config: OptionConfigItem[] = [
    {
      name: 'showHeader',
      display: 'Hide Header',
      description: 'Hides the chat component header',
      value: false
    },
    {
      name: 'hideAddFiles',
      display: 'Hide Add Files',
      description: 'Hides the attachment button',
      value: false
    },
    {
      name: 'hideShareLocation',
      display: 'Hide Share Location',
      description: 'Hides the share location button',
      value: false
    },
    {
      name: 'hideShareAudio',
      display: 'Hide Share Audio',
      description: 'Hides the share audio button',
      value: false
    },
    {
      name: 'hideEmojis',
      display: 'Hide Emojis',
      description: 'Hides the emoji button',
      value: false
    },
    {
      name: 'showCharCounter',
      display: 'Show Character Counter',
      description: 'Shows a character counter in the main text area, up to the max input character limit',
      value: false
    },
    {
      name: 'hideCommands',
      display: 'Hide Commands',
      description: "Hides the command menu from popping up on '/'",
      value: false
    },
    {
      name: 'hiddenCommands',
      display: 'Hidden Commands',
      description:
        'List of commands to filter out, comparing to commandId. Available: invite, join, kick, leave, rename, shrug, who, workflow (join, kick, leave, rename, and who are already hidden by default, so only invite, shrug, and workflow are meaningful here)',
      value: '',
      textField: true,
      hideCheckbox: true,
      textFieldPlaceholder: 'e.g. invite, shrug, workflow'
    },
    {
      name: 'hideMentions',
      display: 'Hide @mention Menu',
      description: "Hides the mention menu from popping up on '@'",
      value: false
    },
    {
      name: 'hideMessageOptions',
      display: 'Hide message options',
      description: 'Hides the message options shown when hovering over a message',
      value: false
    },
    {
      name: 'hideMessageOptionsReaction',
      display: 'Hide reaction option',
      description: 'Hides the reaction option in the message options',
      value: false
    },
    {
      name: 'hideMessageOptionsReply',
      display: 'Hide reply option',
      description: 'Hides the reply option in the message options',
      value: false
    },
    {
      name: 'hideMessageOptionsInfo',
      display: 'Hide Message Options Info',
      description: 'Hides the message info option in the more-options dropdown',
      value: false
    },
    {
      name: 'hideMessageOptionsPreviewPdf',
      display: 'Hide PDF preview option',
      description: 'Hides the preview PDF option in the message options',
      value: false
    },
    {
      name: 'hideTranslatedBody',
      display: 'Hide translated body',
      description: 'Hides the translate option in the message options',
      value: false
    },
    {
      name: 'showMessageOptionsEdit',
      display: 'Hide Message Options Edit',
      description: 'Disables the ability to edit your own messages',
      value: false
    },
    {
      name: 'showMessageOptionsDelete',
      display: 'Hide Message Options Delete',
      description: 'Disables the ability to delete your own messages',
      value: false
    },
    {
      name: 'showMessageOptionsSelect',
      display: 'Hide Message Options Select',
      description: 'Disables the option to select multiple messages/attachments for deletion',
      value: false
    },
    {
      name: 'smsMode',
      display: 'SMS Mode',
      description: 'Plain-text mode used by Mitel One for SMS conversations',
      value: false
    }
  ];

  constructor(
    @Optional() public ref: DynamicDialogRef | null,
    @Optional() public dialogConfig: DynamicDialogConfig | null,
    private formBuilder: UntypedFormBuilder,
    private settingsService: SettingsService,
    public aiConfigService: AiConfigService
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.setPreviousSettings();
    this.loadAiConversationOptions();
  }

  ngOnDestroy() {
    // Auto-save on navigating away instead of a Save button.
    this.setUserSettings();
    this.saveAiConversationOptions();
    this.aiConfigService.save();
  }

  private loadAiConversationOptions(): void {
    const config = this.aiConfigService.config;
    this.aiQuickReplies = !!config.quickReplies;
    this.aiGreetingText = config.greeting?.text ?? '';
    this.aiGreetingOptions = (config.greeting?.options ?? []).join('\n');
    this.aiHandoffLabel = config.handoff?.label ?? '';
  }

  private saveAiConversationOptions(): void {
    const options = this.aiGreetingOptions
      .split('\n')
      .map(option => option.trim())
      .filter(option => option);
    this.aiConfigService.setAiOptions({
      quickReplies: this.aiQuickReplies,
      greeting: this.aiGreetingText.trim() ? { text: this.aiGreetingText.trim(), options } : null,
      handoff: this.aiHandoffLabel.trim() ? { label: this.aiHandoffLabel.trim() } : null
    });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      value: '',
      password: ''
    });
  }

  closeModal() {
    this.ref?.close('Modal Closed');
  }

  public setUserSettings() {
    const options: Record<string, boolean | string[]> = {};

    for (const item of this.config) {
      options[item.name] = item.textField ? this.splitValues(item.value as string) : (item.value as boolean);
    }

    options['showHeader'] = !options['showHeader'];
    options['showMessageOptionsEdit'] = !options['showMessageOptionsEdit'];
    options['showMessageOptionsDelete'] = !options['showMessageOptionsDelete'];
    options['showMessageOptionsSelect'] = !options['showMessageOptionsSelect'];

    localStorage.setItem('chatPageOptions', JSON.stringify(options));
    this.settingsService.updateSettings();
  }

  private setPreviousSettings() {
    let prefs = JSON.parse(localStorage.getItem('chatPageOptions'));

    if (prefs) {
      prefs['showHeader'] = !prefs['showHeader'];
      prefs['showMessageOptionsEdit'] = !prefs['showMessageOptionsEdit'];
      prefs['showMessageOptionsDelete'] = !prefs['showMessageOptionsDelete'];
      prefs['showMessageOptionsSelect'] = !prefs['showMessageOptionsSelect'];

      for (const item of this.config) {
        item.value = item.textField ? this.bunchValues(prefs[item.name]) : !!prefs[item.name];
      }
    }
  }

  private splitValues(valueString: string) {
    valueString.trim();
    return valueString.split(',');
  }

  private bunchValues(valueArray) {
    let valueString: string = valueArray[0];

    for (var i = 1; i < valueArray.length; i++) {
      valueString += ',' + valueArray[i];
    }

    valueString.trim();

    return valueString;
  }

  public getConfigIndex(name: string) {
    for (var i = 0; i < this.config.length; i++) {
      if (this.config[i].name == name) {
        return i;
      }
    }
    return null;
  }

  public setConfigValue(name: string, value: boolean) {
    this.config[this.getConfigIndex(name)].value = value;
  }

  public getConfigValue(name: string) {
    return this.config[this.getConfigIndex(name)].value;
  }
}
